import { useCallback, useEffect, useState } from 'react';
import { Link, useParams, useSearchParams } from 'react-router-dom';
import {
  Alert,
  Button,
  Card,
  Form,
  Input,
  Modal,
  Space,
  Spin,
  Switch,
  Table,
  Tabs,
  Tag,
  Typography,
  message as antdMessage,
} from 'antd';
import {
  ApiOutlined,
  ArrowLeftOutlined,
  ThunderboltOutlined,
  DeleteOutlined,
  EyeOutlined,
  InboxOutlined,
  PartitionOutlined,
  RedoOutlined,
  SaveOutlined,
  SettingOutlined,
  TeamOutlined,
  UsergroupAddOutlined,
  SendOutlined,
} from '@ant-design/icons';
import type { ColumnsType } from 'antd/es/table';
import type { Space as SpaceDto, View } from '@lens/shared';
import { ItemTypesTab } from '../item-types/ItemTypesTab.tsx';
import { JiraSyncTab } from '../jira/JiraSyncTab.tsx';
import { HermesConfigTab } from '../hermes/HermesConfigTab.tsx';
import { AutomationRulesSection } from '../automation/AutomationRulesSection.tsx';
import { TelegramTab } from '../telegram/TelegramTab.tsx';
import { deleteView, listArchivedViews, updateView } from '../views/api.ts';
import { SpaceIconPicker } from '../../shared/components/SpaceIconPicker.tsx';
import { SpaceAvatar } from '../../shared/components/SpaceAvatar.tsx';
import { ApiError } from '../../shared/api/client.ts';
import {
  archiveSpace,
  deleteSpace,
  getSpace,
  resolveSpaceSlug,
  saveSpaceAsTemplate,
  unarchiveSpace,
  updateSpace,
} from './api.ts';
import { MembersTab } from './members/MembersTab.tsx';
import { UserGroupsTab } from './members/UserGroupsTab.tsx';
import { emitSpacesChanged } from '../../shared/lib/spacesBus.ts';
import styles from './SpaceSettingsPage.module.css';
import { useDocumentTitle } from '../../shared/hooks/useDocumentTitle.ts';

// ─── Общий таб ────────────────────────────────────────────────────────────

interface GeneralTabProps {
  space: SpaceDto;
  onUpdated: (space: SpaceDto) => void;
}

interface GeneralFormValues {
  name: string;
  description?: string;
  icon?: string | null;
}

interface SaveTemplateFormValues {
  name: string;
  description?: string;
}

function GeneralTab({ space, onUpdated }: GeneralTabProps): JSX.Element {
  const [form] = Form.useForm<GeneralFormValues>();
  const nameValue = Form.useWatch('name', form);
  const [templateForm] = Form.useForm<SaveTemplateFormValues>();
  const [saving, setSaving] = useState(false);
  const [archiving, setArchiving] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [templateModalOpen, setTemplateModalOpen] = useState(false);
  const [savingTemplate, setSavingTemplate] = useState(false);

  const isAdmin = space.myRole === 'admin';

  const openTemplateModal = (): void => {
    templateForm.setFieldsValue({ name: space.name, description: undefined });
    setTemplateModalOpen(true);
  };

  const handleSaveTemplate = async (): Promise<void> => {
    let values: SaveTemplateFormValues;
    try {
      values = await templateForm.validateFields();
    } catch {
      return;
    }
    setSavingTemplate(true);
    try {
      await saveSpaceAsTemplate({
        spaceId: space.id,
        name: values.name.trim(),
        description: values.description?.trim() || undefined,
      });
      antdMessage.success('Шаблон сохранён');
      setTemplateModalOpen(false);
    } catch (err) {
      antdMessage.error(
        err instanceof ApiError ? err.message : 'Не удалось сохранить шаблон',
      );
    } finally {
      setSavingTemplate(false);
    }
  };

  useEffect(() => {
    form.setFieldsValue({
      name: space.name,
      description: space.description ?? undefined,
      icon: space.icon ?? null,
    });
  }, [space, form]);

  const handleSave = async (): Promise<void> => {
    let values: GeneralFormValues;
    try {
      values = await form.validateFields();
    } catch {
      return;
    }
    setSaving(true);
    setErrorMessage(null);
    try {
      const updated = await updateSpace(space.id, {
        name: values.name.trim(),
        description: values.description?.trim() || null,
        icon: values.icon ?? null,
      });
      onUpdated(updated);
    } catch (err) {
      setErrorMessage(
        err instanceof ApiError ? err.message : 'Не удалось сохранить изменения',
      );
    } finally {
      setSaving(false);
    }
  };

  const doArchiveToggle = async (): Promise<void> => {
    setArchiving(true);
    setErrorMessage(null);
    try {
      if (space.isArchived) {
        await unarchiveSpace(space.id);
        onUpdated({ ...space, isArchived: false });
        antdMessage.success('Пространство восстановлено из архива');
      } else {
        await archiveSpace(space.id);
        onUpdated({ ...space, isArchived: true });
        antdMessage.success('Пространство заархивировано');
      }
      // Рельс/главная грузят пространства независимо — обновляем их.
      emitSpacesChanged();
    } catch (err) {
      setErrorMessage(
        err instanceof ApiError ? err.message : 'Не удалось изменить статус архивации',
      );
    } finally {
      setArchiving(false);
    }
  };

  const handleArchive = (): void => {
    // Разархивация безопасна и обратима — выполняем сразу. Архивацию
    // подтверждаем: она скрывает пространство из активных списков.
    if (space.isArchived) {
      void doArchiveToggle();
      return;
    }
    Modal.confirm({
      title: 'Заархивировать пространство?',
      content:
        'Пространство скроется из активных списков. Его можно будет восстановить из архива.',
      okText: 'Заархивировать',
      cancelText: 'Отмена',
      onOk: () => doArchiveToggle(),
    });
  };

  const handleDelete = (): void => {
    Modal.confirm({
      title: 'Удалить пространство?',
      content: 'Это действие необратимо. Пространство должно быть заархивировано.',
      okText: 'Удалить',
      okButtonProps: { danger: true },
      cancelText: 'Отмена',
      onOk: async () => {
        setDeleting(true);
        setErrorMessage(null);
        try {
          await deleteSpace(space.id);
          window.location.href = '/';
        } catch (err) {
          setErrorMessage(
            err instanceof ApiError ? err.message : 'Не удалось удалить пространство',
          );
          setDeleting(false);
        }
      },
    });
  };

  return (
    <Space direction="vertical" size="large" style={{ width: '100%' }}>
      {errorMessage ? (
        <Alert
          type="error"
          message={errorMessage}
          showIcon
          data-testid="settings-general-error"
        />
      ) : null}

      <Card className={styles.formCard} title="Основные данные">
        <Form<GeneralFormValues>
          form={form}
          layout="vertical"
          requiredMark={false}
          disabled={saving}
        >
          <Form.Item label="Иконка" name="icon" valuePropName="value">
            <SpaceIconPicker name={nameValue ?? space.name} />
          </Form.Item>
          <Form.Item
            label="Название"
            name="name"
            rules={[
              { required: true, message: 'Укажите название' },
              { max: 120, message: 'Не более 120 символов' },
            ]}
          >
            <Input data-testid="settings-name-input" />
          </Form.Item>
          <Form.Item
            label="Описание"
            name="description"
            rules={[{ max: 1000, message: 'Не более 1000 символов' }]}
          >
            <Input.TextArea rows={3} data-testid="settings-description-input" />
          </Form.Item>
          <Form.Item style={{ marginBottom: 0 }}>
            <Button
              type="primary"
              loading={saving}
              onClick={() => void handleSave()}
              data-testid="settings-save-button"
            >
              Сохранить
            </Button>
          </Form.Item>
        </Form>
      </Card>

      {isAdmin ? (
        <Card className={styles.formCard} title="Доступ">
          <Space direction="vertical" size="small" style={{ width: '100%' }}>
            <Space align="center" size="middle">
              <Switch
                checked={space.publicAccess}
                loading={saving}
                onChange={async (checked) => {
                  setSaving(true);
                  setErrorMessage(null);
                  try {
                    const updated = await updateSpace(space.id, {
                      publicAccess: checked,
                    });
                    onUpdated(updated);
                    // Пространство появляется/исчезает в списках у всех —
                    // обновляем рельс/главную текущего пользователя.
                    emitSpacesChanged();
                  } catch (err) {
                    setErrorMessage(
                      err instanceof ApiError ? err.message : 'Не удалось сохранить настройку',
                    );
                  } finally {
                    setSaving(false);
                  }
                }}
                data-testid="settings-public-access-toggle"
              />
              <Typography.Text strong>Открытый доступ — видно всем</Typography.Text>
            </Space>
            <Typography.Text type="secondary">
              Когда включено — пространство видно всем пользователям без явного
              добавления в участники. Каждый получает роль «Участник»
              (может просматривать и редактировать записи, но не менять настройки
              пространства). Явно добавленным участникам и администраторам их роли
              сохраняются.
            </Typography.Text>
          </Space>
        </Card>
      ) : null}

      <Card className={styles.formCard} title="Интеграции">
        <Space direction="vertical" size="small" style={{ width: '100%' }}>
          <Space align="center" size="middle">
            <Switch
              checked={space.jiraIntegrationEnabled}
              loading={saving}
              onChange={async (checked) => {
                setSaving(true);
                setErrorMessage(null);
                try {
                  const updated = await updateSpace(space.id, {
                    jiraIntegrationEnabled: checked,
                  });
                  onUpdated(updated);
                } catch (err) {
                  setErrorMessage(
                    err instanceof ApiError ? err.message : 'Не удалось сохранить настройку',
                  );
                } finally {
                  setSaving(false);
                }
              }}
              data-testid="settings-jira-toggle"
            />
            <Typography.Text strong>Интеграция с Jira</Typography.Text>
          </Space>
          <Typography.Text type="secondary">
            Когда включено — становятся доступны вкладка «Jira», push/pull задач,
            привязка записей к Jira-issues и связанные кнопки в таблице.
          </Typography.Text>

          <Space align="center" size="middle">
            <Switch
              checked={space.hermesIntegrationEnabled}
              loading={saving}
              onChange={async (checked) => {
                setSaving(true);
                setErrorMessage(null);
                try {
                  const updated = await updateSpace(space.id, {
                    hermesIntegrationEnabled: checked,
                  });
                  onUpdated(updated);
                } catch (err) {
                  setErrorMessage(
                    err instanceof ApiError ? err.message : 'Не удалось сохранить настройку',
                  );
                } finally {
                  setSaving(false);
                }
              }}
              data-testid="settings-hermes-toggle"
            />
            <Typography.Text strong>Оценка трудоёмкости (Hermes)</Typography.Text>
          </Space>
          <Typography.Text type="secondary">
            Когда включено — становится доступна вкладка «Оценка» (там же — общий
            контекст для оценки), запуск оценки из карточки записи и групповая
            оценка. Оценка считается внешней системой Hermes асинхронно и
            записывается в поля записи.
          </Typography.Text>
        </Space>
      </Card>

      {isAdmin ? (
        <Card className={styles.formCard} title="Шаблоны">
          <Space direction="vertical" size="small" style={{ width: '100%' }}>
            <Typography.Text type="secondary">
              Сохраните текущую конфигурацию пространства (типы, поля, представления,
              справочники) как шаблон, чтобы быстро создавать похожие пространства.
            </Typography.Text>
            <Button
              icon={<SaveOutlined />}
              onClick={openTemplateModal}
              data-testid="settings-save-template-button"
            >
              Сохранить как шаблон
            </Button>
          </Space>
        </Card>
      ) : null}

      <Modal
        title="Сохранить как шаблон"
        open={templateModalOpen}
        onCancel={() => setTemplateModalOpen(false)}
        onOk={() => void handleSaveTemplate()}
        okText="Сохранить"
        cancelText="Отмена"
        confirmLoading={savingTemplate}
        destroyOnClose
      >
        <Form<SaveTemplateFormValues>
          form={templateForm}
          layout="vertical"
          requiredMark={false}
          disabled={savingTemplate}
        >
          <Form.Item
            label="Название шаблона"
            name="name"
            rules={[
              { required: true, message: 'Укажите название' },
              { max: 200, message: 'Не более 200 символов' },
            ]}
          >
            <Input data-testid="settings-template-name-input" autoFocus />
          </Form.Item>
          <Form.Item
            label="Описание"
            name="description"
            rules={[{ max: 1000, message: 'Не более 1000 символов' }]}
          >
            <Input.TextArea
              rows={3}
              data-testid="settings-template-description-input"
              placeholder="Зачем нужен этот шаблон, для каких пространств подойдёт"
            />
          </Form.Item>
        </Form>
      </Modal>

      <Card className={styles.dangerZone}>
        <Typography.Title level={5} className={styles.dangerTitle}>
          <InboxOutlined style={{ marginRight: 6 }} />
          Архивация и удаление
        </Typography.Title>
        <Typography.Paragraph type="secondary" style={{ marginBottom: 16 }}>
          {space.isArchived
            ? 'Пространство заархивировано. Разархивируйте, чтобы снова сделать его активным.'
            : 'Архивация скрывает пространство из активных списков. Удаление возможно только после архивации.'}
        </Typography.Paragraph>
        <Space>
          <Button
            onClick={() => void handleArchive()}
            loading={archiving}
            data-testid="settings-archive-button"
          >
            {space.isArchived ? 'Разархивировать' : 'Архивировать'}
          </Button>
          {space.isArchived ? (
            <Button
              danger
              icon={<DeleteOutlined />}
              loading={deleting}
              onClick={handleDelete}
              data-testid="settings-delete-button"
            >
              Удалить пространство
            </Button>
          ) : null}
        </Space>
      </Card>
    </Space>
  );
}

// ─── Таб «Представления» ──────────────────────────────────────────────────

interface ViewsTabProps {
  spaceId: string;
}

function ViewsTab({ spaceId }: ViewsTabProps): JSX.Element {
  const [views, setViews] = useState<View[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [restoringId, setRestoringId] = useState<string | null>(null);
  const [deletingId, setDeletingId] = useState<string | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await listArchivedViews(spaceId);
      setViews(res.views);
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Не удалось загрузить представления');
    } finally {
      setLoading(false);
    }
  }, [spaceId]);

  useEffect(() => {
    void load();
  }, [load]);

  const handleRestore = async (viewId: string) => {
    setRestoringId(viewId);
    try {
      await updateView(spaceId, viewId, { isArchived: false });
      setViews((prev) => prev.filter((v) => v.id !== viewId));
      antdMessage.success('Представление восстановлено');
    } catch (err) {
      antdMessage.error(err instanceof ApiError ? err.message : 'Не удалось восстановить представление');
    } finally {
      setRestoringId(null);
    }
  };

  const handleDelete = (viewId: string, viewName: string) => {
    Modal.confirm({
      title: `Удалить представление «${viewName}»?`,
      content: 'Это действие необратимо.',
      okText: 'Удалить',
      okButtonProps: { danger: true },
      cancelText: 'Отмена',
      onOk: async () => {
        setDeletingId(viewId);
        try {
          await deleteView(spaceId, viewId);
          setViews((prev) => prev.filter((v) => v.id !== viewId));
          antdMessage.success('Представление удалено');
        } catch (err) {
          antdMessage.error(err instanceof ApiError ? err.message : 'Не удалось удалить представление');
        } finally {
          setDeletingId(null);
        }
      },
    });
  };

  const columns: ColumnsType<View> = [
    {
      title: 'Название',
      key: 'name',
      render: (_, record) => (
        <Space size={6}>
          {record.icon ? <span>{record.icon}</span> : null}
          <span>{record.name}</span>
        </Space>
      ),
    },
    {
      title: 'Тип',
      dataIndex: 'viewType',
      key: 'viewType',
      width: 120,
      render: (t: string) => (t === 'kanban' ? 'Канбан' : 'Таблица'),
    },
    {
      title: '',
      key: 'actions',
      width: 220,
      render: (_, record) => (
        <Space size={8}>
          <Button
            size="small"
            icon={<RedoOutlined />}
            loading={restoringId === record.id}
            onClick={() => void handleRestore(record.id)}
          >
            Восстановить
          </Button>
          <Button
            size="small"
            danger
            icon={<DeleteOutlined />}
            loading={deletingId === record.id}
            onClick={() => handleDelete(record.id, record.name)}
          >
            Удалить
          </Button>
        </Space>
      ),
    },
  ];

  return (
    <Space direction="vertical" size="large" style={{ width: '100%' }}>
      {error ? <Alert type="error" message={error} showIcon /> : null}

      <Card className={styles.formCard} title="Архивированные представления">
        {loading ? (
          <div style={{ textAlign: 'center', padding: 32 }}>
            <Spin />
          </div>
        ) : views.length === 0 ? (
          <Typography.Text type="secondary">Архивированных представлений нет</Typography.Text>
        ) : (
          <Table<View>
            rowKey="id"
            columns={columns}
            dataSource={views}
            pagination={false}
            size="small"
          />
        )}
      </Card>
    </Space>
  );
}

// ─── Корневой компонент страницы ──────────────────────────────────────────

export function SpaceSettingsPage(): JSX.Element {
  // Роут доступен как `/s/:spaceSlug/settings` (канон) и legacy
  // `/spaces/:id/settings`. `id` — рабочий uuid; при slug-роуте резолвим.
  const { id: routeId, spaceSlug: routeSlug } = useParams<{ id?: string; spaceSlug?: string }>();
  const [searchParams, setSearchParams] = useSearchParams();
  const [id, setId] = useState<string | undefined>(routeId);
  const [space, setSpace] = useState<SpaceDto | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useDocumentTitle(space?.name, 'Настройки');

  useEffect(() => {
    if (routeId) {
      setId(routeId);
      return;
    }
    if (!routeSlug) return;
    let cancelled = false;
    resolveSpaceSlug(routeSlug)
      .then((r) => {
        if (!cancelled) setId(r.id);
      })
      .catch((err) => {
        if (cancelled) return;
        setError(err instanceof ApiError ? err.message : 'Пространство не найдено');
        setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [routeId, routeSlug]);

  useEffect(() => {
    if (!id) return;
    setLoading(true);
    getSpace(id)
      .then((s) => {
        setSpace(s);
        setLoading(false);
      })
      .catch((err) => {
        setError(err instanceof ApiError ? err.message : 'Пространство не найдено');
        setLoading(false);
      });
  }, [id]);

  if (loading) {
    return (
      <div
        style={{ display: 'flex', justifyContent: 'center', padding: 64 }}
        data-testid="settings-loading"
      >
        <Spin size="large" />
      </div>
    );
  }

  if (error || !space) {
    return (
      <Alert
        type="error"
        message={error ?? 'Пространство не найдено'}
        showIcon
        data-testid="settings-error"
      />
    );
  }

  const tabItems = [
    {
      key: 'general',
      label: (
        <span>
          <SettingOutlined style={{ marginRight: 6 }} />
          Общее
        </span>
      ),
      children: (
        <GeneralTab
          space={space}
          onUpdated={(updated) => setSpace(updated)}
        />
      ),
    },
    {
      key: 'members',
      label: (
        <span>
          <TeamOutlined style={{ marginRight: 6 }} />
          Участники
        </span>
      ),
      children: <MembersTab spaceId={space.id} myRole={space.myRole} />,
    },
    {
      key: 'user-groups',
      label: (
        <span>
          <UsergroupAddOutlined style={{ marginRight: 6 }} />
          Группы пользователей
        </span>
      ),
      children: <UserGroupsTab spaceId={space.id} myRole={space.myRole} />,
    },
    {
      key: 'item-types',
      label: (
        <span>
          <PartitionOutlined style={{ marginRight: 6 }} />
          Типы и поля
        </span>
      ),
      children: <ItemTypesTab spaceId={space.id} myRole={space.myRole} jiraEnabled={space.jiraIntegrationEnabled} />,
    },
    {
      key: 'views',
      label: (
        <span>
          <EyeOutlined style={{ marginRight: 6 }} />
          Представления
        </span>
      ),
      children: <ViewsTab spaceId={space.id} />,
    },
    ...(space.jiraIntegrationEnabled
      ? [
          {
            key: 'jira',
            label: (
              <span>
                <ApiOutlined style={{ marginRight: 6 }} />
                Jira
              </span>
            ),
            children: <JiraSyncTab spaceId={space.id} myRole={space.myRole} />,
          },
        ]
      : []),
    ...(space.hermesIntegrationEnabled
      ? [
          {
            key: 'hermes',
            label: (
              <span>
                <ThunderboltOutlined style={{ marginRight: 6 }} />
                Оценка
              </span>
            ),
            children: (
              <HermesConfigTab
                spaceId={space.id}
                myRole={space.myRole}
                commonContext={space.hermesCommonContext ?? null}
                onCommonContextSaved={(updated) => setSpace(updated)}
              />
            ),
          },
        ]
      : []),
    {
      key: 'automation',
      label: (
        <span>
          <ThunderboltOutlined style={{ marginRight: 6 }} />
          Автоматизации
        </span>
      ),
      children: <AutomationRulesSection spaceId={space.id} myRole={space.myRole} jiraEnabled={space.jiraIntegrationEnabled} />,
    },
    // Вкладка только для админа пространства: все эндпоинты модуля
    // требуют роль admin, участнику там нечего делать.
    ...(space.myRole === 'admin'
      ? [
          {
            key: 'telegram',
            label: (
              <span>
                <SendOutlined style={{ marginRight: 6 }} />
                Телеграм
              </span>
            ),
            children: <TelegramTab spaceId={space.id} myRole={space.myRole} />,
          },
        ]
      : []),
  ];

  // Активная вкладка управляется через ?tab= в URL — чтобы прямые ссылки
  // (например, CTA «Создать тип записи» с пустого пространства) открывали
  // нужную вкладку, а не всегда «Общее». Неизвестный ключ → «Общее».
  const requestedTab = searchParams.get('tab');
  const activeTabKey = tabItems.some((t) => t.key === requestedTab)
    ? (requestedTab as string)
    : 'general';

  return (
    <div>
      <Link to={`/s/${space.slug}`} className={styles.backLink}>
        <ArrowLeftOutlined />
        {space.name}
      </Link>

      <div className={styles.pageHeader}>
        <h2
          className={styles.pageTitle}
          data-testid="settings-page-title"
          style={{ display: 'inline-flex', alignItems: 'center', gap: 8 }}
        >
          <SpaceAvatar name={space.name} icon={space.icon} seed={space.id} size={28} />
          {space.name}
        </h2>
        {space.isArchived ? <Tag color="orange" className={styles.archiveTag}>Архив</Tag> : null}
      </div>

      <Tabs
        activeKey={activeTabKey}
        onChange={(key) => {
          setSearchParams(
            (prev) => {
              const next = new URLSearchParams(prev);
              if (key === 'general') next.delete('tab');
              else next.set('tab', key);
              return next;
            },
            { replace: true },
          );
        }}
        items={tabItems}
        data-testid="settings-tabs"
      />
    </div>
  );
}
